import type { Analysis } from '../../types/models';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Star, CheckCircle, XCircle, AlertTriangle, FileText } from 'lucide-react';

interface AnalysisWithRelations extends Analysis {
  candidates?: { name: string } | null;
  jobs?: { title: string } | null;
}

interface AnalysisCardProps {
  analysis: AnalysisWithRelations;
  onClick?: () => void;
}

const getScoreProps = (score: number) => {
  if (score >= 8) {
    return { 
      variant: "default" as const, 
      className: "bg-green-100 text-green-800 border-green-200 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700/40 dark:hover:bg-green-800/30",
      textColor: "text-green-600"
    };
  } 
  if (score >= 4) {
     return { 
       variant: "default" as const, 
       className: "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700/40 dark:hover:bg-yellow-800/30",
       textColor: "text-yellow-600"
     };
  } 
  return { 
    variant: "destructive" as const, 
    className: "bg-red-100 text-red-800 border-red-200 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700/40 dark:hover:bg-red-800/30",
    textColor: "text-red-600"
  };
};

const getVerdictDisplay = (verdict: string) => {
  switch (verdict.toUpperCase()) {
    case 'STRONG_FIT':
      return {
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: <CheckCircle className="h-3 w-3" />,
        label: 'Strong Fit'
      };
    case 'POTENTIAL_FIT':
      return {
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: <Star className="h-3 w-3" />,
        label: 'Potential Fit'
      };
    case 'NEEDS_CONSIDERATION':
      return {
        color: 'bg-amber-100 text-amber-800 border-amber-200',
        icon: <AlertTriangle className="h-3 w-3" />,
        label: 'Needs Review'
      };
    case 'NOT_RECOMMENDED':
      return {
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: <XCircle className="h-3 w-3" />,
        label: 'Not Recommended'
      };
    default:
      return {
        color: 'bg-slate-100 text-slate-800 border-slate-200',
        icon: <FileText className="h-3 w-3" />,
        label: 'Unknown'
      };
  }
};

export function AnalysisCard({ analysis, onClick }: AnalysisCardProps) {
  const candidateName = analysis.candidates?.name || 'Unknown Candidate';
  const jobTitle = analysis.jobs?.title || 'Unknown Position';
  const { textColor: scoreTextColor } = getScoreProps(analysis.fit_score);
  const verdictDisplay = getVerdictDisplay(analysis.verdict);
  const analysisDate = analysis.created_at ? new Date(analysis.created_at).toLocaleDateString() : 'Unknown Date';
  
  // Extract first letters for candidate initials
  const candidateInitials = candidateName.split(' ').map(n => n[0]).join('').toUpperCase();
  
  // Extract technical skills for display
  const technicalSkills = analysis.technical_skills || [];
  const skillsArray = Array.isArray(technicalSkills) 
    ? technicalSkills.map((ts: any) => ts.skill || ts.name || String(ts)).filter(Boolean)
    : [];

  return (
    <Card 
      onClick={onClick} 
      className={cn(
        "transition-all hover:shadow-lg bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/60", 
        onClick ? "cursor-pointer hover:border-primary/30 group" : ""
      )}
    >
      <CardContent className="p-6">
        <div className="space-y-4">
          {/* Header: Candidate, Job, Score */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-slate-600">
                  {candidateInitials}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-slate-900 truncate">{candidateName}</h4>
                <p className="text-sm text-slate-600 truncate">{jobTitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="text-right">
                <span className={cn("text-2xl font-bold", scoreTextColor)}>
                  {analysis.fit_score}
                </span>
                <p className="text-xs text-slate-500">Fit Score</p>
              </div>
            </div>
          </div>

          {/* Verdict and Date */}
          <div className="flex items-center gap-2">
            <Badge className={verdictDisplay.color}>
              {verdictDisplay.icon}
              <span className="ml-1">{verdictDisplay.label}</span>
            </Badge>
            <span className="text-xs text-slate-500">
              {analysisDate}
            </span>
          </div>

          {/* Skills Preview */}
          {skillsArray.length > 0 && (
            <div>
              <p className="text-sm font-medium text-slate-900 mb-2 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                Key Skills ({skillsArray.length})
              </p>
              <div className="flex flex-wrap gap-1">
                {skillsArray.slice(0, 3).map((skill, index) => (
                  <Badge key={index} variant="outline" className="text-xs bg-green-50 border-green-200 text-green-800">
                    {skill}
                  </Badge>
                ))}
                {skillsArray.length > 3 && (
                  <Badge variant="outline" className="text-xs bg-slate-50 border-slate-200 text-slate-600">
                    +{skillsArray.length - 3} more
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* View Details Hint */}
          {onClick && (
            <div className="pt-2 border-t border-slate-100">
              <p className="text-xs text-slate-500 group-hover:text-primary transition-colors">
                Click to view detailed analysis →
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}